import { Box, LanguageOutlinedIcon } from '@cmg/design-system';
import React from 'react';

import { onDark } from '../consentSurface';
import claudeMark from '../logos/claude.webp';
import openaiMark from '../logos/openai-logo.png';

/**
 * A mark we ship ourselves, chosen by publisher host. This is never the client's `logo_uri`:
 * ADR 0001's invariant is that the consent page loads no remote asset, because that URL is
 * supplied by the client document. The host keys are the same ones hardcoded in
 * `CimdConstants.TrustedClientIdPrefixes`, so nothing about the document decides what renders.
 *
 * Anything unrecognised — including every `clients.json` client, which has no publisher host at
 * all — gets the generic globe, so the two lists drifting degrades rather than breaks.
 */
type Mark = {
  src: string;
  /** True when the asset carries its own background and should fill the tile edge to edge. */
  fullBleed?: boolean;
};

/**
 * How much *ink* every mark in the identity row renders, publisher and CMG alike. Sizing the boxes
 * instead of the ink is what let the row drift to four different scales — a full-bleed logo filled
 * its 64px box, a trimmed one filled 56 of its 56, and the globe inked only 47 of its 56.
 *
 * Each mark's box is derived from this by how much of its own box the artwork covers. Publisher
 * bitmaps are expected to be trimmed to their ink; one shipped with transparent padding would
 * render short, since `contain` fits the canvas and not the artwork.
 */
export const MARK_INK = 46;

/** MUI's outlined icons draw across ~0.83 of their font size; the rest is the glyph's own margin. */
const MUI_ICON_INK_RATIO = 0.834;

// A Map, not an object literal: `marksByHost['constructor']` on the latter returns a truthy
// prototype member and renders an `img` with no `src` instead of falling back to the globe.
const marksByHost = new Map<string, Mark>([
  ['claude.ai', { src: claudeMark, fullBleed: true }],
  ['chatgpt.com', { src: openaiMark }],
]);

export type PublisherMarkProps = {
  host?: string;
};

export const PublisherMark: React.FC<PublisherMarkProps> = ({ host }) => {
  const mark = host ? marksByHost.get(host) : undefined;

  if (!mark) {
    // A step down from the marks we do ship, because a bright, confident glyph in the identity row
    // reads as an assertion about an app we cannot vouch for. The caution itself is stated in words
    // beneath it; this only stops the placeholder from out-asserting them.
    return (
      <LanguageOutlinedIcon sx={{ ...onDark.mutedText, fontSize: MARK_INK / MUI_ICON_INK_RATIO }} />
    );
  }

  return (
    <Box
      component="img"
      src={mark.src}
      alt=""
      sx={{
        width: MARK_INK,
        height: MARK_INK,
        // An asset carrying its own background is clipped to a circle so it reads as a mark rather
        // than a pasted-on square; a transparent one just sits on the shell. No upward correction
        // for the disc: the usual one assumes a circle loses area to its neighbours' corners, and
        // these neighbours are sparse marks that a solid saturated fill already outweighs.
        ...(mark.fullBleed
          ? { objectFit: 'cover', borderRadius: '50%' }
          : { objectFit: 'contain' }),
      }}
    />
  );
};

export default PublisherMark;
